/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useState } from "react";
import { useVisiblePolling } from "../lib/useVisiblePolling";
import { api } from "../lib/apiClient";
import { StatusBadge } from "../components/StatusBadge";
import { Camera, ShieldAlert, Trash2 } from "lucide-react";
import { PhotoStrip } from "../components/PhotoStrip";
import PhotoUploadButton from "../components/PhotoUploadButton";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";
import PeopleTagger, { PeoplePills } from "../components/PeopleTagger";

const SEV = ["low", "medium", "high", "critical"];
const CAT = ["Access Control", "Medical", "Fire", "Structural", "Crowd", "Broadcast", "Other"];

export default function IncidentsPage() {
  const { selectedMatchId } = useMatch();
  const DEMO = selectedMatchId;

  const [items, setItems] = useState([]);
  const [preMatchDepts, setPreMatchDepts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileRefs = useRef({});
  const [form, setForm] = useState({
    location: "",
    category: "Access Control",
    severity: "medium",
    description: "",
    immediate_action: "",
    assigned_dept: "",
    tagged_people: [],
  });

  const load = async () => {
    if (!DEMO) return;
    const [{ data: inc }, { data: deps }] = await Promise.all([
      api.get(`/matches/${DEMO}/incidents`),
      api.get(`/reference/pre-match-departments?match_id=${DEMO}`),
    ]);
    setItems(inc);
    const sortedDepts = [...deps.departments].sort((a, b) => a.localeCompare(b));
    setPreMatchDepts(sortedDepts);
    setForm((f) => ({ ...f, assigned_dept: f.assigned_dept || sortedDepts[0] || "" }));
  };
  // Poll only while the tab is visible; 30 s cadence — incidents change slowly.
  useVisiblePolling(load, 30000, [DEMO]);

  const resolve = async (id) => {
    await api.patch(`/incidents/${id}`, { status: "resolved", outcome: "Resolved by command centre" });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this incident log entry?")) return;
    await api.delete(`/incidents/${id}`);
    load();
  };

  const create = async (e) => {
    e.preventDefault();
    await api.post(`/matches/${DEMO}/incidents`, form);
    setForm({ ...form, location: "", description: "", immediate_action: "", tagged_people: [] });
    setShowNew(false);
    load();
  };

  const uploadPhoto = async (id, file) => {
    setUploading(id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/incidents/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } finally { setUploading(null); }
  };

  if (!selectedMatchId) return <RequireMatch />;
  return (
    <div className="p-6 md:p-10 pb-20" data-testid="page-incidents">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 border-b border-cmdBorder pb-6">
        <div>
          <div className="mc-label">Escalations · Alerts · Response</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">Critical Incident Log</h1>
        </div>
        <button
          onClick={() => setShowNew((v) => !v)}
          className="bg-incidentRed text-white px-4 py-2 font-bold uppercase tracking-widest text-xs hover:bg-red-600 flex items-center gap-2"
          data-testid="incident-new-btn"
        >
          <ShieldAlert className="w-4 h-4" /> {showNew ? "Cancel" : "Log Incident"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="mc-panel p-5 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="incident-form">
          <input className="bg-black border border-cmdBorder p-2 text-sm md:col-span-2" placeholder="Location" required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="incident-form-loc" />
          <select className="bg-black border border-cmdBorder p-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CAT.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className="bg-black border border-cmdBorder p-2 text-sm" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} data-testid="incident-form-sev">
            {SEV.map((s) => <option key={s}>{s}</option>)}
          </select>
          <textarea rows={2} className="bg-black border border-cmdBorder p-2 text-sm md:col-span-4" placeholder="Description" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="incident-form-desc" />
          <input className="bg-black border border-cmdBorder p-2 text-sm md:col-span-2" placeholder="Immediate action" value={form.immediate_action} onChange={(e) => setForm({ ...form, immediate_action: e.target.value })} />
          <div className="md:col-span-2">
            <label className="mc-label">Assigned Dept</label>
            <select
              className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
              value={form.assigned_dept}
              onChange={(e) => setForm({ ...form, assigned_dept: e.target.value })}
              data-testid="incident-form-dept"
              required
            >
              {preMatchDepts.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="md:col-span-4">
            <label className="mc-label">Tag Individuals (from Contacts)</label>
            <div className="mt-1">
              <PeopleTagger
                value={form.tagged_people}
                onChange={(v) => setForm({ ...form, tagged_people: v })}
                placeholder="Add responders / commanders on the ground…"
                testidPrefix="incident-tagger"
              />
            </div>
          </div>
          <button type="submit" className="bg-readyGreen text-white font-bold uppercase tracking-widest text-xs py-2 md:col-span-1" data-testid="incident-form-submit">+ Log Incident</button>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((i) => {
          const isCritical = i.severity === "critical" || i.severity === "high";
          const borderColor = i.status === "resolved" ? "#34C759" : isCritical ? "#FF3B30" : "#FFCC00";
          return (
            <div
              key={i.id}
              className="mc-panel border-l-4 p-4"
              style={{ borderLeftColor: borderColor }}
              data-testid={`incident-card-${i.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="font-mono-live text-sm text-zinc-400">{i.number}</span>
                  <StatusBadge status={i.status === "resolved" ? "resolved" : "open"} kind="incident" live={i.status !== "resolved"} />
                </div>
                <div className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border" style={{ color: borderColor, borderColor }}>
                  {i.severity}
                </div>
              </div>
              <div className="font-head text-2xl font-bold uppercase mt-3 tracking-tight leading-tight">{i.location}</div>
              <div className="text-xs text-zinc-500 mt-1">{i.category} · Reported by {i.reported_by}</div>
              <div className="mt-3 text-sm">{i.description}</div>
              {i.immediate_action && <div className="text-xs text-zinc-300 mt-2 border-l-2 border-cmdBorder pl-2">Action: {i.immediate_action}</div>}
              <PhotoStrip paths={i.photos} testidPrefix={`incident-photos-${i.id}`} />
              <div className="mt-3 text-xs">
                <span className="mc-label">Assigned</span>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span>{i.assigned_dept || "—"}</span>
                  {i.tagged_people?.length > 0 && (
                    <>
                      <span className="text-zinc-600">·</span>
                      <PeoplePills people={i.tagged_people} testidPrefix={`incident-tagged-${i.id}`} />
                    </>
                  )}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <PhotoUploadButton
                  onUpload={(file) => uploadPhoto(i.id, file)}
                  label={`Photo (${i.photos?.length || 0})`}
                  testid={`incident-upload-${i.id}`}
                />
                {i.status !== "resolved" && (
                  <button
                    onClick={() => resolve(i.id)}
                    className="border border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-white px-3 py-2 text-xs uppercase tracking-widest transition-colors"
                    data-testid={`incident-resolve-${i.id}`}
                  >
                    Mark Resolved
                  </button>
                )}
                <button
                  onClick={() => remove(i.id)}
                  className="ml-auto flex items-center gap-1 border border-cmdBorder hover:border-incidentRed hover:text-incidentRed px-3 py-2 text-xs uppercase tracking-widest transition-colors"
                  data-testid={`incident-delete-${i.id}`}
                  title="Delete incident"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="lg:col-span-2 text-zinc-500 p-8">No incidents logged.</div>}
      </div>
    </div>
  );
}
